import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import {
  PlayerSearchViewAttributes,
  StatsOnboarding,
} from '@sorare/core/src/lib/playerSearchView';

import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

type Props = {
  children: ReactNode;
  step: StatsOnboarding;
  placement: 'top' | 'bottom' | 'left' | 'right';
};

const MaxWidth = styled.div`
  max-width: 280px;
`;

const messages = defineMessages({
  [StatsOnboarding.INIT]: {
    id: 'PlayerSearchHelp.init',
    defaultMessage: 'Toggle options to compare players.',
  },
  [StatsOnboarding.SORT_BY_CLICKING]: {
    id: 'PlayerSearchHelp.sortByClicking',
    defaultMessage: 'Click any stat to sort players',
  },
  [StatsOnboarding.COMBINE_SORTS]: {
    id: 'PlayerSearchHelp.combineSorts',
    defaultMessage: 'Hold shift and click another stat to combine sorts',
  },
  [StatsOnboarding.FILTERS]: {
    id: 'PlayerSearchHelp.filters',
    defaultMessage: 'Try filtering players',
  },
});

export const PlayerSearchHelp = ({
  children,
  step,
  placement,
}: Props): ReactNode => {
  const { statsManager } = useManagePlayerSearchView();
  const { currentUser } = useCurrentUserContext();
  const onboardingOpen =
    statsManager[PlayerSearchViewAttributes.ONBOARDING] === step;

  if (currentUser && onboardingOpen && step in messages) {
    return (
      <Tooltip
        zIndex={1}
        skip={!onboardingOpen}
        open={onboardingOpen}
        placement={placement}
        variant="brand"
        title={
          <MaxWidth>
            <BodyS bold>
              <FormattedMessage {...messages[step as keyof typeof messages]} />
            </BodyS>
          </MaxWidth>
        }
      >
        {children}
      </Tooltip>
    );
  }
  return children;
};
